import { Input, Tooltip } from 'antd';
import React, { useState } from 'react';

type MaskedInputNumberProps = {
  value: string | number | null;
  onChange: (value: string | null) => void;
  onSuccess?: (payload: { value: number }) => void;
  isAllowed?: (v: number) => boolean;
  disabled?: boolean;
  errorText?: string;
};

const sanitize = (raw: string) => {
  let s = raw.replace(/[^0-9./]/g, '');

  const dotIndex = s.indexOf('.');
  if (dotIndex !== -1) {
    s =
      s.slice(0, dotIndex + 1) +
      s
        .slice(dotIndex + 1)
        .replace(/\./g, '')
        .slice(0, 4);
  }

  const slashIndex = s.indexOf('/');
  if (slashIndex !== -1) {
    const before = s.slice(0, slashIndex);
    const after = s.slice(slashIndex + 1).replace(/\//g, '');

    s = `${before}/${after}`;
  }

  return s;
};

const validate = (s: string): number | null => {
  if (!s) return null;

  if (s.includes('/')) {
    const [num, den] = s.split('/');

    if (!num || !den) return null;
    if (den === '0') return null;

    const n = Number(num);
    const d = Number(den);
    if (isNaN(n) || isNaN(d)) return null;

    return n / d;
  }

  const num = Number(s);
  if (isNaN(num)) return null;

  return num;
};

export const MaskedInputNumber: React.FC<MaskedInputNumberProps> = ({
  value,
  onChange,
  onSuccess,
  isAllowed,
  disabled,
  errorText = 'Недопустимое значение',
}) => {
  const [isInvalid, setIsInvalid] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitize(e.target.value);
    setIsInvalid(false); // сбрасываем ошибку при вводе
    onChange(sanitized || null);
  };

  const handleBlur = () => {
    if (!value) return;

    const numeric = validate(String(value));
    if (numeric == null) return;

    if (isAllowed && !isAllowed(numeric)) {
      setIsInvalid(true);
      return;
    }

    setIsInvalid(false);
    onSuccess?.({ value: numeric });
  };

  const input = (
    <Input
      value={value ?? ''}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      status={isInvalid ? 'error' : undefined}
    />
  );

  return isInvalid ? (
    <Tooltip title={errorText} placement="topLeft">
      {input}
    </Tooltip>
  ) : (
    input
  );
};
